import { SubscriptionClient } from 'subscriptions-transport-ws';

let isConnecting = false;
let wsClient: SubscriptionClient | null = null;
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const HEARTBEAT_INTERVAL = 30000; // 30 seconds

// Store message handlers for direct subscription
type MessageHandler = (data: any) => void;
const messageHandlers: Map<string, Set<MessageHandler>> = new Map();

const clearHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const startHeartbeat = (client: SubscriptionClient) => {
  clearHeartbeat();
  heartbeatInterval = setInterval(() => {
    if (client.status === 1) { // WebSocket.OPEN
      try {
        (client as any).client?.send(JSON.stringify({ type: 'ka' }));
      } catch (e) {
        console.warn('Heartbeat failed, connection may be stale');
      }
    }
  }, HEARTBEAT_INTERVAL);
};

// Setup message interception on the WebSocket client
const setupMessageInterception = (client: SubscriptionClient) => {
  console.log('🔌 Setting up message interception on WebSocket client');
  
  // Use the client's internal event system to intercept messages
  const originalProcessReceivedData = (client as any).processReceivedData;
  if (originalProcessReceivedData) {
    console.log('🔌 Found processReceivedData, overriding it');
    (client as any).processReceivedData = function(receivedData: any) {
      try {
        // Parse if string
        let parsedData = receivedData;
        if (typeof receivedData === 'string') {
          try {
            parsedData = JSON.parse(receivedData);
          } catch (e) {
            // Not JSON, use as-is
          }
        }
        
        console.log('🔌 WebSocket processReceivedData:', JSON.stringify(parsedData).substring(0, 500));
        
        // Dispatch to registered handlers based on subscription type
        if (parsedData.type === 'data' && parsedData.payload?.data) {
          const dataKeys = Object.keys(parsedData.payload.data);
          const registeredKeys = Array.from(messageHandlers.keys());
          console.log('🔌 WebSocket dataKeys:', dataKeys, 'registered handlers:', registeredKeys, 'handlers map size:', messageHandlers.size);
          
          dataKeys.forEach(key => {
            const handlers = messageHandlers.get(key);
            console.log(`🔌 Looking for handlers for key "${key}":`, handlers ? `found ${handlers.size}` : 'not found');
            
            if (handlers && handlers.size > 0) {
              console.log(`🔌 Dispatching to ${handlers.size} handlers for ${key}`);
              handlers.forEach(handler => {
                try {
                  handler(parsedData.payload.data[key]);
                } catch (e) {
                  console.error('Handler error:', e);
                }
              });
            } else {
              console.log(`🔌 No handlers registered for ${key}`);
            }
          });
        } else {
          console.log('🔌 Message is not a data message or has no payload.data');
        }
      } catch (e) {
        console.error('Message interception error:', e);
      }
      
      // Call original handler
      return originalProcessReceivedData.call(this, receivedData);
    };
  } else {
    console.warn('🔌 processReceivedData not found on client, trying alternative interception');
    // Alternative: intercept at WebSocket level
    const checkAndSetupWsInterception = () => {
      const ws = (client as any).client;
      if (ws && !ws._intercepted) {
        ws._intercepted = true;
        const originalOnMessage = ws.onmessage;
        ws.onmessage = function(event: MessageEvent) {
          try {
            const data = JSON.parse(event.data);
            console.log('🔌 WebSocket onmessage intercepted:', data);
            
            if (data.type === 'data' && data.payload?.data) {
              const dataKeys = Object.keys(data.payload.data);
              console.log('🔌 WebSocket dataKeys:', dataKeys, 'registered handlers:', Array.from(messageHandlers.keys()));
              dataKeys.forEach(key => {
                const handlers = messageHandlers.get(key);
                if (handlers && handlers.size > 0) {
                  console.log(`🔌 Dispatching to ${handlers.size} handlers for ${key}`);
                  handlers.forEach(handler => {
                    try {
                      handler(data.payload.data[key]);
                    } catch (e) {
                      console.error('Handler error:', e);
                    }
                  });
                }
              });
            }
          } catch (e) {
            // Not JSON or error
          }
          
          if (originalOnMessage) {
            originalOnMessage.call(this, event);
          }
        };
      }
    };
    
    // Check periodically until WebSocket is available
    const intervalId = setInterval(() => {
      if ((client as any).client) {
        checkAndSetupWsInterception();
        clearInterval(intervalId);
      }
    }, 100);
    
    // Also setup on connect
    client.on('connected', checkAndSetupWsInterception);
    client.on('reconnected', checkAndSetupWsInterception);
  }
};

const wsClientService = async (token: string) => {
  if (isConnecting || wsClient) {
    return wsClient;
  }

  isConnecting = true;

  const bearerToken = 'Bearer ' + token;
  const wsUrl = `${process.env.REACT_APP_AWS_WEBSOCKET_URL!}?Authorization=${encodeURIComponent(bearerToken)}`;

  wsClient = new SubscriptionClient(wsUrl, {
    reconnect: true,
    timeout: 30000,
    lazy: true,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
    connectionCallback: (error: any) => {
      if (error) {
        console.error('🔌 WebSocket connection callback error:', error);
      } else {
        console.log('🔌 WebSocket connection callback success');
        reconnectAttempts = 0;
      }
    },
    inactivityTimeout: 0
  });

  // Setup message interception
  setupMessageInterception(wsClient);

  wsClient.on('connected', () => {
    console.log('🔌 WebSocket connected');
    isConnecting = false;
    reconnectAttempts = 0;
    startHeartbeat(wsClient!);
  });

  wsClient.on('reconnected', () => {
    console.log('🔌 WebSocket reconnected');
    reconnectAttempts = 0;
    startHeartbeat(wsClient!);
  });

  wsClient.on('reconnecting', () => {
    reconnectAttempts++;
    console.log(`🔌 WebSocket reconnecting... attempt ${reconnectAttempts}`);
  });

  wsClient.on('error', (error: any) => {
    console.error('🔌 WebSocket error:', error);
    isConnecting = false;
  });

  wsClient.on('disconnected', () => {
    console.warn('🔌 WebSocket disconnected');
    clearHeartbeat();
    isConnecting = false;
  });

  wsClient.on('close', (event: any) => {
    console.warn('🔌 WebSocket closed:', event);
    clearHeartbeat();
    isConnecting = false;
  });

  return wsClient;
};

// Register a handler for a specific subscription type
export const registerMessageHandler = (subscriptionName: string, handler: MessageHandler) => {
  if (!messageHandlers.has(subscriptionName)) {
    messageHandlers.set(subscriptionName, new Set());
  }
  messageHandlers.get(subscriptionName)!.add(handler);
  
  // Log all registered handlers for debugging
  const allHandlers = Array.from(messageHandlers.entries()).map(([key, set]) => `${key}: ${set.size}`);
  console.log(`🔌 Registered handler for ${subscriptionName}, total handlers: ${messageHandlers.get(subscriptionName)!.size}, all handlers: [${allHandlers.join(', ')}]`);
  
  // Return unsubscribe function
  return () => {
    const handlers = messageHandlers.get(subscriptionName);
    if (handlers) {
      handlers.delete(handler);
      const allHandlersAfter = Array.from(messageHandlers.entries()).map(([key, set]) => `${key}: ${set.size}`);
      console.log(`🔌 Unregistered handler for ${subscriptionName}, remaining: ${handlers.size}, all handlers: [${allHandlersAfter.join(', ')}]`);
    }
  };
};

export const closeWebSocketConnection = () => {
  clearHeartbeat();
  messageHandlers.clear();
  if (wsClient) {
    wsClient.close();
    wsClient = null;
  }
  isConnecting = false;
  reconnectAttempts = 0;
};

export const getWsClient = () => wsClient;

export default wsClientService;
